import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressIndicator from './components/ProgressIndicator';
import BasicInfoStep from './components/BasicInfoStep';
import SkillsStep from './components/SkillsStep';
import EducationStep from './components/EducationStep';
import PreferencesStep from './components/PreferencesStep';
import ReviewStep from './components/ReviewStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const JobSeekerRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    // Basic Info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    
    // Skills & Experience
    desiredPosition: '',
    experienceLevel: '',
    skills: [],
    professionalSummary: '',
    
    // Education & Languages
    education: [],
    languages: [
      { id: 1, language: 'norwegian', proficiency: 'native' },
      { id: 2, language: 'english', proficiency: 'fluent' }
    ],
    certifications: '',
    
    // Preferences
    location: '',
    availability: '',
    workPermit: '',
    employmentTypes: [],
    workArrangements: [],
    minSalary: '',
    maxSalary: '',
    portfolio: '',
    additionalInfo: '',
    
    // Privacy Settings
    allowSearch: true,
    emailNotifications: true,
    allowShortlist: true
  });

  const steps = [
    { id: 1, title: 'Basic Info', component: BasicInfoStep },
    { id: 2, title: 'Skills', component: SkillsStep },
    { id: 3, title: 'Education', component: EducationStep },
    { id: 4, title: 'Preferences', component: PreferencesStep },
    { id: 5, title: 'Review', component: ReviewStep }
  ];

  useEffect(() => {
    // Save draft to localStorage
    localStorage.setItem('jobSeekerDraft', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    // Load draft from localStorage on mount
    const savedDraft = localStorage.getItem('jobSeekerDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(parsedDraft);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
        if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
        if (!formData?.email?.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Email is invalid';
        if (!formData?.password) newErrors.password = 'Password is required';
        else if (formData?.password?.length < 8) newErrors.password = 'Password must be at least 8 characters';
        if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
        break;

      case 2:
        if (!formData?.desiredPosition?.trim()) newErrors.desiredPosition = 'Desired position is required';
        if (!formData?.experienceLevel) newErrors.experienceLevel = 'Experience level is required';
        if (formData?.skills?.length === 0) newErrors.skills = 'At least one skill is required';
        break;

      case 4:
        if (!formData?.location) newErrors.location = 'Location preference is required';
        if (!formData?.availability) newErrors.availability = 'Availability is required';
        if (!formData?.workPermit) newErrors.workPermit = 'Work authorization status is required';
        if (!formData?.employmentTypes?.length) newErrors.employmentTypes = 'Select at least one employment type';
        if (!formData?.workArrangements?.length) newErrors.workArrangements = 'Select at least one work arrangement';
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleExit = () => {
    const confirmExit = window.confirm(
      'Are you sure you want to exit? Your progress will be saved as a draft.'
    );
    if (confirmExit) {
      navigate('/homepage');
    }
  };

  const clearDraft = () => {
    localStorage.removeItem('jobSeekerDraft');
  };

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;

  return (
    <div className="min-h-screen bg-background">
      <ProgressIndicator 
        currentStep={currentStep} 
        totalSteps={steps?.length} 
        steps={steps} 
      />
      <div className="container mx-auto px-6 py-8">
        {/* Exit Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleExit}
          >
            Exit
          </Button>
        </div>

        {/* Step Content */}
        <div className="bg-card rounded-2xl shadow-elevation p-8">
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        </div>

        {/* Draft Notice */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-muted-foreground bg-muted/30 px-4 py-2 rounded-lg">
            <Icon name="Save" size={16} />
            <span>Your progress is automatically saved</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerRegistration;